import { UpdateExamReportResponseDTO } from 'src/app/modules/Recruitment/types/license-exam-types';

export enum ErrorCode {
  MANDATORY_FIELD_ERROR = 'mandatory_field_error',
  EXAM_DATE_FORMAT_ERROR = 'exam_date_format_error',
  EXAM_DATE_PERIOD_ERROR = 'exam_date_period_error',
  START_TIME_END_TIME_PERIOD_ERROR = 'startTime_endTime_period_error',
  START_TIME_FORMAT_ERROR = 'startTime_format_error',
  END_TIME_FORMAT_ERROR = 'endTime_format_error',
  REGISTRATION_NOT_FOUND = 'registration_not_found',
  APPLICANT_NOT_FOUND = 'applicant_not_found',
  SCHEDULE_NOT_FOUND = 'schedule_not_found',
  INVALID_EXAM_RESULT = 'invalid_exam_result',
  MISSING_MANDATORY_COLUMN = 'missing_mandatory_column',
  MULTIPLE_REGISTRATION_FOUND = 'multiple_registration_found',
  DUPLICATE_ROW = 'duplicate_row',
  /**
   * exam name should be a map {"zh-Hans": "exam name in Chinese", "en": "exam name in English"}
   */
  EXAM_NAME_FORMAT_ERROR = 'exam_name_format_error',

  // =================== PHKL ========================//
  INCORRECT_PASSWORD = 'incorrect_password',
  FILE_CONTENT_ERROR = 'file_content_error',
  /**
   * PHKL Specific: ID_Doc_No should be at least 3 characters
   */
  ID_NUMBER_TOO_SHORT = 'id_number_too_short',
}

type ErrorMessageParams = {
  data?: any;
  rowNum?: number;
};

const ERROR_MESSAGES = {
  [ErrorCode.MANDATORY_FIELD_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    const missingColumns = params?.data?.columns;
    const columnsText = missingColumns.join(', ');
    return `${t('row_number', { row: params?.rowNum })}: ${t('no_blank_mandatory_fields_allowed', {
      fields: columnsText,
    })}`;
  },

  [ErrorCode.EXAM_DATE_FORMAT_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    const format = params?.data?.format;
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('incorrect_date_format_should_be_format', { format: format })}`;
  },

  [ErrorCode.EXAM_DATE_PERIOD_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('exam_Date_should_no_be_later_than_document_upload_date')}`;
  },

  [ErrorCode.START_TIME_END_TIME_PERIOD_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('end_Time_should_not_be_earlier_than_Start_Time_on_row', { row: params?.rowNum })}`;
  },

  [ErrorCode.START_TIME_FORMAT_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    const format = params?.data?.format;
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('incorrect_start_time_format', { format: format })}`;
  },

  [ErrorCode.END_TIME_FORMAT_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    const format = params?.data?.format;
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('incorrect_end_time_format', { format: format })}`;
  },

  [ErrorCode.REGISTRATION_NOT_FOUND]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('did_not_find_existed_registration_with_the_given_info')}`;
  },

  [ErrorCode.APPLICANT_NOT_FOUND]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('did_not_find_existed_applicant_with_the_given_info')}`;
  },

  [ErrorCode.SCHEDULE_NOT_FOUND]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('did_not_find_existed_exam_schedule_with_the_given_info')}`;
  },

  [ErrorCode.INVALID_EXAM_RESULT]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('invalid_result')}`;
  },

  [ErrorCode.EXAM_NAME_FORMAT_ERROR]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('exam_name_format_error')}`;
  },

  [ErrorCode.INCORRECT_PASSWORD]: (params?: ErrorMessageParams, t?: any) => t('please_update_password'),

  [ErrorCode.FILE_CONTENT_ERROR]: (params?: ErrorMessageParams, t?: any) => t('failed_to_read_file'),

  [ErrorCode.ID_NUMBER_TOO_SHORT]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('ID_Doc_No_should_be_at_least_3_characters_long')}`;
  },

  [ErrorCode.MISSING_MANDATORY_COLUMN]: (params?: ErrorMessageParams, t?: any) => {
    const columns = params?.data?.columns;
    const columnsStr = columns?.join(', ');

    return `${t('missing_mandatory_columns')}: ${columnsStr}`;
  },
  [ErrorCode.MULTIPLE_REGISTRATION_FOUND]: (params?: ErrorMessageParams, t?: any) => {
    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('multiple_registration_found')}`;
  },
  [ErrorCode.DUPLICATE_ROW]: (params?: ErrorMessageParams, t?: any) => {
    const columns = params?.data?.duplicateRowNumbers;
    const columnsStr = columns?.join(', ');

    return `${t('row_number', {
      row: params?.rowNum,
    })}: ${t('duplicate_applicants_records_found_for_same_exam_schedule_on_row')}: ${columnsStr} , ${t(
      'you_can_edit_exam_result_in_Registration_Detail_page',
    )}`;
  },
} satisfies {
  [key in ErrorCode]: (params?: ErrorMessageParams) => string;
};

export const getErrorMessage = (errorCode: ErrorCode, params?: ErrorMessageParams, t?: any): string => {
  const handler = ERROR_MESSAGES[errorCode];

  try {
    return handler(params, t);
  } catch (e) {
    return `error:${errorCode}`;
  }
};

export const transformApiErrors = (response: UpdateExamReportResponseDTO, t: any): string[] => {
  return response.errors.map((error) => {
    try {
      const validErrorCode = Object.values(ErrorCode).find((code) => code === error.errorCode);

      if (!validErrorCode) {
        return `error: ${error.errorCode}`;
      }

      return getErrorMessage(
        validErrorCode,
        {
          rowNum: error.rowNum,
          data: error.data,
        },
        t,
      );
    } catch (e) {
      return `error: ${error.errorCode}`;
    }
  });
};
